import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Stack, Lightning, Fire, Target, ArrowRight, Sparkle, ChatCircleDots, CheckCircle } from "@phosphor-icons/react";
import { ConsultBlock } from "@/components/ConsultCTA";
import { useAuth } from "@/context/AuthContext";
import { useAssessments } from "@/hooks/useAssessments";
import api from "@/lib/api";

export default function Overview({ onNavigate }) {
  const { user } = useAuth();
  const { assessments } = useAssessments();
  const [profile, setProfile] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/profile").then((r) => setProfile(r.data)),
      api.get("/challenges").then((r) => setChallenges(r.data.challenges)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-32"><div className="h-9 w-9 animate-spin rounded-full border-2 border-slate-800 border-t-teal-400" /></div>;

  const stats = profile?.stats || {};
  const primaries = profile?.primaries || [];
  const completedSlugs = new Set(primaries.map((p) => p.slug));
  const activeChallenges = challenges.filter((c) => c.progress && c.progress.status === "active");
  const nextAssessment = assessments.find((a) => !completedSlugs.has(a.slug));

  const STAT = [
    { label: "Assessments", value: `${stats.assessments_completed || 0}/${stats.assessments_total || 12}`, icon: Stack, color: "#14B8A6" },
    { label: "Patterns Found", value: stats.patterns_identified || 0, icon: Target, color: "#F59E0B" },
    { label: "Challenges Done", value: stats.challenges_completed || 0, icon: Lightning, color: "#A78BFA" },
    { label: "Day Streak", value: stats.streak || 0, icon: Fire, color: "#FB7185" },
  ];

  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-500">Your living profile</p>
      <h1 className="mt-2 font-heading text-3xl font-light tracking-tight text-slate-50 sm:text-4xl">Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.</h1>
      <p className="mt-2 text-base text-slate-400">{profile?.headline_insight ? <>Your most recent pattern: <span className="text-teal-300">{profile.headline_insight}</span>.</> : "Take your first assessment to bring your profile to life."}</p>

      {/* Progress snapshot */}
      <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="progress-snapshot">
        {STAT.map((s) => (
          <div key={s.label} className="rounded-2xl border border-slate-800 bg-slate-900 p-5">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ background: `${s.color}1a`, color: s.color }}><s.icon size={18} weight="duotone" /></span>
            <p className="mt-3 font-heading text-3xl font-semibold text-slate-100">{s.value}</p>
            <p className="text-[11px] uppercase tracking-wider text-slate-500">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Next best action */}
      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-2 rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-900 to-slate-950 p-7" data-testid="next-best-action">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-500">Your next best step</p>
          {nextAssessment ? (
            <>
              <h3 className="mt-3 font-heading text-2xl font-medium text-slate-100">Take the {nextAssessment.short} assessment</h3>
              <p className="mt-2 max-w-lg text-sm text-slate-400">{nextAssessment.tagline} Each new assessment sharpens your profile and your AI coach.</p>
              <Link to={`/a/${nextAssessment.slug}`} className="mt-5 inline-flex items-center gap-2 rounded-full bg-teal-500 px-6 py-3 text-sm font-semibold text-slate-950 hover:bg-teal-400" data-testid="next-action-btn">Start now <ArrowRight size={16} weight="bold" /></Link>
            </>
          ) : (
            <>
              <h3 className="mt-3 font-heading text-2xl font-medium text-slate-100">You've mapped your whole profile.</h3>
              <p className="mt-2 max-w-lg text-sm text-slate-400">Talk to your AI coach about the patterns you've uncovered, or revisit one to track how you've changed.</p>
              <button onClick={() => onNavigate("coach")} className="mt-5 inline-flex items-center gap-2 rounded-full bg-teal-500 px-6 py-3 text-sm font-semibold text-slate-950 hover:bg-teal-400" data-testid="next-action-btn">Talk to your coach <ArrowRight size={16} weight="bold" /></button>
            </>
          )}
        </motion.div>

        <button onClick={() => onNavigate("coach")} className="group flex flex-col items-start rounded-2xl border border-slate-800 bg-slate-900 p-6 text-left transition-all hover:-translate-y-0.5 hover:border-teal-500/40" data-testid="home-go-coach">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-teal-500/10 text-teal-400"><ChatCircleDots size={20} weight="duotone" /></span>
          <p className="mt-4 font-heading text-base font-medium text-slate-100">Talk to your AI Coach</p>
          <p className="mt-1 text-sm text-slate-500">It remembers every assessment you've taken.</p>
          <span className="mt-auto pt-4 inline-flex items-center gap-1.5 text-sm font-medium text-teal-400">Open coach <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" /></span>
        </button>
      </div>

      {/* Recent reports */}
      <div className="mt-8">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-medium text-slate-200">Recent results</h2>
          <button onClick={() => onNavigate("reports")} className="text-sm text-teal-400 hover:text-teal-300">View all</button>
        </div>
        {primaries.length === 0 ? (
          <div className="mt-4 rounded-2xl border border-dashed border-slate-800 p-10 text-center">
            <Sparkle size={28} weight="duotone" className="mx-auto text-slate-600" />
            <p className="mt-3 text-sm text-slate-500">No results yet. Take your first assessment.</p>
            <button onClick={() => onNavigate("assessments")} className="mt-4 inline-flex items-center gap-2 rounded-full bg-teal-500 px-5 py-2.5 text-sm font-semibold text-slate-950 hover:bg-teal-400">Browse assessments</button>
          </div>
        ) : (
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {primaries.slice(0, 6).map((p) => (
              <Link key={p.result_id} to={`/results/${p.result_id}`} className="group rounded-2xl border border-slate-800 bg-slate-900 p-5 transition-all hover:-translate-y-0.5 hover:border-slate-700" data-testid={`recent-result-${p.slug}`}>
                <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: p.primary_color }} /><p className="text-xs uppercase tracking-wider text-slate-500">{p.assessment_name}</p></div>
                <p className="mt-2 font-heading text-lg font-medium" style={{ color: p.primary_color }}>{p.primary_name}</p>
                <span className="mt-3 inline-flex items-center gap-1.5 text-sm text-teal-400">View report <ArrowRight size={13} className="transition-transform group-hover:translate-x-1" /></span>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Active challenges */}
      {activeChallenges.length > 0 && (
        <div className="mt-8">
          <h2 className="font-heading text-lg font-medium text-slate-200">Active challenges</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {activeChallenges.map((ch) => {
              const done = ch.progress.completed_days.length;
              const pct = Math.round((done / ch.duration_days) * 100);
              return (
                <button key={ch.challenge_id} onClick={() => onNavigate("challenges")} className="rounded-2xl border border-slate-800 bg-slate-900 p-5 text-left transition-all hover:border-slate-700">
                  <p className="font-heading text-base font-medium text-slate-100">{ch.title}</p>
                  <div className="mt-3 flex items-center gap-3"><div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-800"><div className="h-full rounded-full bg-teal-400" style={{ width: `${pct}%` }} /></div><span className="font-mono text-xs text-slate-500">{done}/{ch.duration_days}</span></div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Consult */}
      <div className="mt-8"><ConsultBlock source="dashboard-home" /></div>
    </div>
  );
}
